import type { ToolCallMessagePart } from '@assistant-ui/react';
import { Loader } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import cx from 'clsx';
import { AnimatePresence } from 'motion/react';
import { Children, isValidElement, type JSXElementConstructor, type ReactElement, type ReactNode } from 'react';

import { isDeclarativeComplete, resolveCompleteToken, resolveSlot } from './resolve-spec.tsx';
import { ToolAwaitingInput } from './ToolAwaitingInput.tsx';
import { ToolContent } from './ToolContent.tsx';
import classes from './ToolDisplay.module.css';
import { ToolStatus } from './ToolStatus.tsx';
import { HudBox } from '#plugin-chat/components/hud/HudBox.tsx';
import { Shimmer } from '#plugin-chat/components/shared/Shimmer.tsx';
import { usePartStatus } from '#plugin-chat/hooks/usePartStatus.ts';
import { useToolDisplaySpec } from '#plugin-chat/hooks/useToolDisplaySpec.ts';
import { useToolPart } from '#plugin-chat/hooks/useToolPart.ts';
import type { ToolDisplaySpec } from '#plugin-chat/lib/tool/message-formatters.ts';

// Slot components
interface RunningSlotProps {
  children: React.ReactNode;
  hideDuringInterrupt?: boolean;
}

function RunningSlot({ children, hideDuringInterrupt: _hideDuringInterrupt = true }: RunningSlotProps) {
  const { isRunning, isAwaitingInput } = usePartStatus();

  if (!isRunning || isAwaitingInput || !children) {
    return null;
  }

  return (
    <>
      <div className={classes.icon}>
        <Loader color="dark.1" size={16} />
      </div>
      <div className={classes.label}>
        <Shimmer color="color-mix(in oklab, var(--mantine-color-dark-3), transparent 80%)" variant="background">
          {children}
        </Shimmer>
      </div>
    </>
  );
}

interface CompleteSlotProps {
  children: React.ReactNode;
  icon?: React.ComponentType<{ size?: number | string; color?: string }>;
  color?: string;
}

function CompleteSlot({ children, icon: Icon = IconCheck, color = 'var(--mantine-color-green-4)' }: CompleteSlotProps) {
  const { isComplete, isCancelled, isError } = usePartStatus();
  if (!isComplete || isCancelled || isError) {
    return null;
  }
  return (
    <>
      <div className={classes.icon}>
        <Icon color={color} size={16} />
      </div>
      <div className={classes.label} style={{ '--status-color': color }}>
        {children}
      </div>
    </>
  );
}

interface ErrorSlotProps {
  children: React.ReactNode;
  icon?: React.ComponentType<{ size?: number; color?: string }>;
  color?: string;
}

function ErrorSlot({ children, icon: Icon = IconX, color = 'var(--mantine-color-red-4)' }: ErrorSlotProps) {
  const { isCancelled, isError } = usePartStatus();
  if (!isCancelled && !isError) {
    return null;
  }
  return (
    <>
      <div className={classes.icon}>
        <Icon color={color} size={16} />
      </div>
      <div className={classes.label}>{children}</div>
    </>
  );
}

interface ContentSlotProps {
  children: React.ReactNode;
  /** Enable shimmer effect during streaming (default: true) */
  shimmer?: boolean;
}

function ContentSlot({ children, shimmer = true }: ContentSlotProps) {
  const { isRunning, isError, isCancelled } = usePartStatus();
  // Bail out on error/cancelled — render components shouldn't paint potentially
  // empty or stale content over an error. The status row (DefaultStatus +
  // ErrorSlot, both driven by usePartStatus) already surfaces the failure.
  if (isError || isCancelled) {
    return null;
  }
  const isStreaming = shimmer && isRunning;

  return (
    <div className={classes.contentBox}>
      <HudBox
        accentHue={240}
        bevel={10}
        cornerLength={60}
        corners={[]}
        cornerThickness={3}
        particleCount={2}
        primaryHue={250}
        strokeWidth={1}
        style={
          {
            '--hud-background-opacity': 0,
            '--hud-glow-opacity': 0,
          } as React.CSSProperties
        }
      >
        <div className={classes.content}>
          <Shimmer
            color="color-mix(in oklab, var(--mantine-color-dark-3), transparent 80%)"
            duration={1000}
            enabled={isStreaming}
            repeatDelay={500}
            style={{ width: '100%' }}
            variant="background"
          >
            {children}
          </Shimmer>
        </div>
      </HudBox>
    </div>
  );
}

interface AwaitingInputSlotProps {
  children?: React.ReactNode;
}

function AwaitingInputSlot({ children }: AwaitingInputSlotProps) {
  return children;
}

/**
 * Synthesise the slot children for {@link ToolDisplay} from a tool's
 * declarative {@link ToolDisplaySpec}. Used by the spec-driven render path —
 * tools that set `display: { … }` (and omit `render`) get this for free.
 *
 * Each slot maps onto an existing subcomponent so the JSX-children path and
 * the spec-driven path produce structurally identical output.
 */
function renderSpecChildren(
  spec: ToolDisplaySpec<Record<string, unknown>, unknown>,
  args: Record<string, unknown>,
  result: unknown,
): ReactNode {
  const runningNode = resolveSlot(spec.running, { args, result });
  const errorNode = resolveSlot(spec.error, { args, result });
  const contentNode = resolveSlot(spec.content, { args, result });

  let completeNode: ReactNode = null;
  if (spec.complete !== undefined) {
    if (isDeclarativeComplete(spec.complete)) {
      const complete = spec.complete;
      const { color, icon } = resolveCompleteToken(complete);
      const label = resolveSlot(complete.label as ReactNode | (() => ReactNode), { args, result });
      const extras = resolveSlot(complete.extras, { args, result });
      completeNode = (
        <ToolDisplay.Complete color={color} icon={icon}>
          {label}
          {extras}
        </ToolDisplay.Complete>
      );
    } else {
      completeNode = <ToolDisplay.Complete>{spec.complete}</ToolDisplay.Complete>;
    }
  }

  return (
    <>
      {runningNode !== undefined && runningNode !== null && <ToolDisplay.Running>{runningNode}</ToolDisplay.Running>}
      {completeNode}
      {errorNode !== undefined && errorNode !== null && <ToolDisplay.Error>{errorNode}</ToolDisplay.Error>}
      {contentNode !== undefined && contentNode !== null && <ToolDisplay.Content>{contentNode}</ToolDisplay.Content>}
    </>
  );
}
export function ToolDisplay({
  children,
}: React.PropsWithChildren<Partial<ToolCallMessagePart<Record<string, unknown>>>>) {
  const status = usePartStatus();
  // Children-less invocation: synthesise the slots from the registered
  // tool's `display` spec. This is the path the framework's default render
  // takes — tools opt in by setting `display: { … }` on their config and
  // omitting `render`.
  const { args, result, toolName } = useToolPart<Record<string, unknown>, unknown>();
  const spec = useToolDisplaySpec(toolName);
  const resolvedChildren = children === undefined && spec ? renderSpecChildren(spec, args ?? {}, result) : children;
  const childArray = Children.toArray(resolvedChildren) as ReactElement<unknown, JSXElementConstructor<unknown>>[];

  const statusChildren = childArray.filter(
    (child) =>
      isValidElement(child) && child.type.name !== ContentSlot.name && child.type.name !== AwaitingInputSlot.name,
  );
  const contentChildren = childArray.filter((child) => isValidElement(child) && child.type.name === ContentSlot.name);
  const awaitingInputChildren = childArray.filter(
    (child) => isValidElement(child) && child.type.name === AwaitingInputSlot.name,
  );

  return (
    <div
      className={cx(
        classes.toolDisplay,
        status.isCancelled && classes.cancelled,
        (status.isComplete || status.isCancelled) && classes.complete,
      )}
    >
      <AnimatePresence>
        <ToolStatus key="tool-status">{statusChildren}</ToolStatus>
        <ToolAwaitingInput key="tool-awaiting-input">{awaitingInputChildren}</ToolAwaitingInput>
        <ToolContent key="tool-content">{contentChildren}</ToolContent>
      </AnimatePresence>
    </div>
  );
}

// Attach slot components to ToolDisplay for compound component pattern
ToolDisplay.Running = RunningSlot;
ToolDisplay.Complete = CompleteSlot;
ToolDisplay.Error = ErrorSlot;
ToolDisplay.Content = ContentSlot;
ToolDisplay.AwaitingInput = AwaitingInputSlot;
